import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useCart } from '../../components/CartContext';
import './design.css';

const Design = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lineWidth, setLineWidth] = useState(5);
  const [lineColor, setLineColor] = useState('#262fd9');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [activeTool, setActiveTool] = useState('brush');
  const { addToCart } = useCart();

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size properly
    canvas.width = 800;
    canvas.height = 600;
    
    // Set initial background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set initial drawing styles
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;
  }, [backgroundColor, lineColor, lineWidth]); // Added dependencies

  // Update canvas when background changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Save current drawing
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Fill with new background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Restore drawing
    ctx.putImageData(imageData, 0, 0);
  }, [backgroundColor]);

  // Update drawing styles when color/size changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = activeTool === 'eraser' ? backgroundColor : lineColor;
    ctx.lineWidth = lineWidth;
  }, [lineColor, lineWidth, activeTool, backgroundColor]);

  const startDrawing = useCallback((e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing]);

  const stopDrawing = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.closePath();
    setIsDrawing(false);
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          
          // Clear canvas and set background
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Calculate dimensions to maintain aspect ratio
          const maxWidth = canvas.width * 0.9;
          const maxHeight = canvas.height * 0.9;
          
          let { width, height } = img;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          // Center the image
          const x = (canvas.width - width) / 2;
          const y = (canvas.height - height) / 2;
          
          ctx.drawImage(img, x, y, width, height);
          setUploadedImage(event.target.result);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setUploadedImage(null);
  };

  const changeBackgroundColor = (color) => {
    setBackgroundColor(color);
  };

  const downloadDesign = () => {
    const canvas = canvasRef.current;
    
    // Create a temporary canvas for high-quality download
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    // Set higher resolution for download
    tempCanvas.width = canvas.width * 2;
    tempCanvas.height = canvas.height * 2;
    
    // Scale and draw with better quality
    tempCtx.scale(2, 2);
    tempCtx.drawImage(canvas, 0, 0);
    
    const link = document.createElement('a');
    link.download = `enerstic-design-${Date.now()}.png`;
    link.href = tempCanvas.toDataURL('image/png', 1.0);
    link.click();
  };

  const addToCartAsCustom = () => {
    const canvas = canvasRef.current;
    const designData = canvas.toDataURL('image/png', 0.8);
    
    const customProduct = {
      id: `custom-${Date.now()}`,
      name: 'Custom Designed Product',
      price: 499.99,
      image: designData,
      category: 'custom',
      type: 'design',
      customDesign: true
    };
    
    addToCart(customProduct);
    alert('🎨 Your custom design has been added to cart!');
  };

  const handleToolChange = (tool) => {
    setActiveTool(tool);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (tool === 'eraser') {
      // Eraser uses background color to "erase"
      ctx.strokeStyle = backgroundColor;
    } else {
      ctx.strokeStyle = lineColor;
    }
  };

  const colorPalette = [
    '#262fd9', '#000000', '#ffffff', '#ff3b30', '#4cd964', 
    '#007aff', '#ffcc00', '#ff2d55', '#5ac8fa'
  ];

  const bgColors = [
    '#ffffff', '#000000', '#f8f9fa', '#262fd9', 
    '#fff0f0', '#f0fff0', '#f0f0ff'
  ];

  // Touch events for mobile
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    canvasRef.current.dispatchEvent(mouseEvent);
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    canvasRef.current.dispatchEvent(mouseEvent);
  }, []);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    const mouseEvent = new MouseEvent('mouseup', {});
    canvasRef.current.dispatchEvent(mouseEvent);
  }, []);

  return (
    <div className="design-page">
      <div className="design-header">
        <h1>Design Studio</h1>
        <p>Create your custom fashion designs - draw, upload images, and create unique patterns</p>
      </div>

      <div className="design-content">
        <div className="tools-panel">
          <div className="tool-section">
            <h3>🛠️ Tools</h3>
            <div className="tool-buttons">
              <button 
                className={`tool-btn ${activeTool === 'brush' ? 'active' : ''}`}
                onClick={() => handleToolChange('brush')}
              >
                🖌️ Brush
              </button>
              <button 
                className={`tool-btn ${activeTool === 'eraser' ? 'active' : ''}`}
                onClick={() => handleToolChange('eraser')}
              >
                🧽 Eraser
              </button>
            </div>
          </div>

          <div className="tool-section">
            <h3>📏 Brush Size: {lineWidth}px</h3>
            <input 
              type="range" 
              min="1" 
              max="50" 
              value={lineWidth} 
              onChange={(e) => setLineWidth(parseInt(e.target.value))}
              className="brush-slider"
            />
            <div className="brush-preview">
              <div 
                className="brush-dot"
                style={{
                  width: `${lineWidth}px`,
                  height: `${lineWidth}px`,
                  backgroundColor: activeTool === 'eraser' ? backgroundColor : lineColor
                }}
              ></div>
            </div>
          </div>

          <div className="tool-section">
            <h3>🎨 Colors</h3>
            <div className="color-palette">
              {colorPalette.map(color => (
                <button
                  key={color}
                  className={`color-swatch ${lineColor === color ? 'active' : ''}`}
                  style={{ 
                    backgroundColor: color,
                    border: color === '#ffffff' ? '2px solid #ddd' : 'none'
                  }}
                  onClick={() => {
                    setLineColor(color);
                    setActiveTool('brush');
                  }}
                  title={color}
                />
              ))}
            </div>
            <div className="color-picker-container">
              <label>Custom Color:</label>
              <input 
                type="color" 
                value={lineColor} 
                onChange={(e) => {
                  setLineColor(e.target.value);
                  setActiveTool('brush');
                }}
                className="color-picker"
              />
            </div>
          </div>

          <div className="tool-section">
            <h3>🏞️ Background</h3>
            <div className="bg-colors">
              {bgColors.map(color => (
                <button
                  key={color}
                  className={`bg-swatch ${backgroundColor === color ? 'active' : ''}`}
                  style={{ 
                    backgroundColor: color,
                    border: color === '#ffffff' ? '2px solid #ddd' : 'none'
                  }}
                  onClick={() => changeBackgroundColor(color)}
                  title={color}
                />
              ))}
            </div>
          </div>

          <div className="tool-section">
            <h3>📁 Upload Image</h3>
            <label className="file-upload-label">
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload}
                className="file-upload"
              />
              <span className="upload-text">📤 Choose Image</span>
            </label>
            {uploadedImage && (
              <button 
                className="remove-image-btn"
                onClick={() => setUploadedImage(null)}
              >
                🗑️ Remove Image
              </button>
            )}
          </div>

          <div className="action-buttons">
            <button className="action-btn clear" onClick={clearCanvas}>
              🗑️ Clear Canvas
            </button>
            <button className="action-btn download" onClick={downloadDesign}>
              💾 Download Design
            </button>
            <button className="action-btn add-to-cart" onClick={addToCartAsCustom}>
              🛒 Add to Cart - R499.99
            </button>
          </div>
        </div>

        <div className="canvas-container">
          <canvas
            ref={canvasRef}
            width={800}
            height={600}
            className="design-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          <div className="canvas-overlay">
            {!uploadedImage && (
              <div className="upload-prompt">
                <div className="prompt-content">
                  <div className="prompt-icon">🎨</div>
                  <h4>Start Creating!</h4>
                  <p>Draw freely or upload an image to begin designing</p>
                  <small>Use the tools on the left to customize your design</small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="design-tips">
        <h3>💡 Design Tips</h3>
        <div className="tips-grid">
          <div className="tip-card">
            <div className="tip-icon">🖼️</div>
            <p>Upload clothing templates or inspiration images to trace over</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">🎯</div>
            <p>Use different brush sizes - small for details, large for backgrounds</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">🌈</div>
            <p>Experiment with background colors to see how your design looks</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">💾</div>
            <p>Download high-quality PNG files of your finished designs</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Design;